import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { User, Mail, Lock, Save, Camera, Upload, Bike, Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import UserTracksSection from '@/components/UserTracksSection';
import OfflineMapManager from '@/components/OfflineMapManager';
import TrackerSetup from '@/components/TrackerSetup';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Profile({ user, onUserUpdate }) {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(user.profile_photo ? `${BACKEND_URL}/api${user.profile_photo}` : null);
  const [motorcycles, setMotorcycles] = useState([]);
  const [showMotoDialog, setShowMotoDialog] = useState(false);
  const [editingMoto, setEditingMoto] = useState(null);
  const [motoFormData, setMotoFormData] = useState({
    brand: '',
    model: '',
    plate: '',
    year: '',
    kilometers: ''
  });
  const [motoDocuments, setMotoDocuments] = useState({
    circulation_permit: null,
    technical_sheet: null,
    insurance: null
  });
  const [formData, setFormData] = useState({
    full_name: user.full_name || '',
    email: user.email || '',
    phone: user.phone || '',
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    fetchMotorcycles();
  }, []);

  // Handle hash-based scrolling
  useEffect(() => {
    if (location.hash) {
      const elementId = location.hash.substring(1); // Remove the '#'
      // Small delay to ensure the DOM is fully rendered
      setTimeout(() => {
        const element = document.getElementById(elementId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location.hash]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate password change
    if (formData.new_password || formData.current_password) {
      if (!formData.current_password) {
        toast.error('Debes ingresar tu contraseña actual');
        return;
      }
      if (formData.new_password !== formData.confirm_password) {
        toast.error('Las contraseñas nuevas no coinciden');
        return;
      }
      if (formData.new_password.length < 6) {
        toast.error('La nueva contraseña debe tener al menos 6 caracteres');
        return;
      }
    }

    setLoading(true);

    try {
      const updateData = {
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone
      };

      if (formData.new_password) {
        updateData.current_password = formData.current_password;
        updateData.new_password = formData.new_password;
      }

      const response = await axios.put(`${API}/users/me`, updateData);
      
      // Update local storage and parent component
      const updatedUser = response.data;
      localStorage.setItem('user', JSON.stringify(updatedUser));
      onUserUpdate(updatedUser);
      
      toast.success('Perfil actualizado exitosamente');
      
      // Clear password fields
      setFormData({
        ...formData,
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar perfil');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona una imagen');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no debe superar los 5MB');
      return;
    }

    setUploadingPhoto(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/users/me/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const photoUrl = `${BACKEND_URL}/api${response.data.profile_photo}`;
      setPhotoPreview(photoUrl);

      // Update user in storage and parent
      const updatedUser = { ...user, profile_photo: response.data.profile_photo };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      onUserUpdate(updatedUser);

      toast.success('Foto de perfil actualizada');
    } catch (error) {
      toast.error('Error al subir la foto');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const fetchMotorcycles = async () => {
    try {
      const response = await axios.get(`${API}/motorcycles`);
      setMotorcycles(response.data);
    } catch (error) {
      console.error('Error loading motorcycles:', error);
    }
  };

  const handleMotoSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('motorcycle_data', JSON.stringify(motoFormData));
      
      // Append documents if provided
      if (motoDocuments.circulation_permit) {
        formData.append('circulation_permit', motoDocuments.circulation_permit);
      }
      if (motoDocuments.technical_sheet) {
        formData.append('technical_sheet', motoDocuments.technical_sheet);
      }
      if (motoDocuments.insurance) {
        formData.append('insurance', motoDocuments.insurance);
      }

      if (editingMoto) {
        await axios.put(`${API}/motorcycles/${editingMoto.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Motocicleta actualizada');
      } else {
        await axios.post(`${API}/motorcycles`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Motocicleta añadida');
      }
      
      fetchMotorcycles();
      setShowMotoDialog(false);
      setEditingMoto(null);
      setMotoFormData({
        brand: '',
        model: '',
        plate: '',
        year: '',
        kilometers: ''
      });
      setMotoDocuments({
        circulation_permit: null,
        technical_sheet: null,
        insurance: null
      });
    } catch (error) {
      toast.error('Error al guardar motocicleta');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditMoto = (moto) => {
    setEditingMoto(moto);
    setMotoFormData({
      brand: moto.brand,
      model: moto.model,
      plate: moto.plate,
      year: moto.year,
      kilometers: moto.kilometers
    });
    setShowMotoDialog(true);
  };

  const handleDeleteMoto = async (motoId) => {
    if (!confirm('¿Estás seguro de eliminar esta motocicleta?')) return;
    
    try {
      await axios.delete(`${API}/motorcycles/${motoId}`);
      toast.success('Motocicleta eliminada');
      fetchMotorcycles();
    } catch (error) {
      toast.error('Error al eliminar motocicleta');
    }
  };

  const handleAddNewMoto = () => {
    setEditingMoto(null);
    setMotoFormData({
      brand: '',
      model: '',
      plate: '',
      year: '',
      kilometers: ''
    });
    setShowMotoDialog(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-gray-100 py-12 px-4">
      <div className="max-w-3xl mx-auto space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-orange-600 to-gray-800 bg-clip-text text-transparent" data-testid="profile-title">
            Mi Perfil
          </h1>
          <p className="text-gray-600 mt-2">Gestiona tu información personal</p>
        </div>

        {/* Profile Card */}
        <Card className="border-0 shadow-xl" data-testid="profile-card">
          <CardHeader>
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="relative group">
                <div className="w-24 h-24 rounded-full overflow-hidden bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
                  {photoPreview ? (
                    <img src={photoPreview} alt="Profile" className="w-full h-full object-cover" data-testid="profile-photo" />
                  ) : (
                    <User className="w-12 h-12 text-white" />
                  )}
                </div>
                <label 
                  htmlFor="photo-upload" 
                  className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                  data-testid="photo-upload-label"
                >
                  {uploadingPhoto ? (
                    <span className="text-white text-xs">Subiendo...</span>
                  ) : (
                    <Camera className="w-6 h-6 text-white" />
                  )}
                </label>
                <input
                  id="photo-upload"
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="hidden"
                  data-testid="photo-upload-input"
                  disabled={uploadingPhoto}
                />
              </div>
              <div className="text-center md:text-left">
                <CardTitle className="text-2xl">{user.full_name}</CardTitle>
                <CardDescription>{user.email}</CardDescription>
                <p className="text-xs text-gray-500 mt-2">
                  Haz clic en la foto para cambiarla
                </p>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <User className="w-5 h-5 text-orange-500" />
                    Información Personal
                  </h3>
                  <Separator className="mb-4" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="full_name">Nombre Completo</Label>
                  <Input
                    id="full_name"
                    data-testid="profile-full-name"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                    <Input
                      id="email"
                      data-testid="profile-email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Teléfono</Label>
                  <Input
                    id="phone"
                    data-testid="profile-phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+34 600 000 000"
                  />
                </div>
              </div>

              {/* Change Password */}
              <div className="space-y-4 pt-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Lock className="w-5 h-5 text-orange-500" />
                    Cambiar Contraseña
                  </h3>
                  <Separator className="mb-4" />
                  <p className="text-sm text-gray-600 mb-4">
                    Deja estos campos vacíos si no deseas cambiar tu contraseña
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="current_password">Contraseña Actual</Label>
                  <Input
                    id="current_password"
                    data-testid="profile-current-password"
                    type="password"
                    value={formData.current_password}
                    onChange={(e) => setFormData({ ...formData, current_password: e.target.value })}
                    placeholder="••••••••"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="new_password">Nueva Contraseña</Label>
                    <Input
                      id="new_password"
                      data-testid="profile-new-password"
                      type="password"
                      value={formData.new_password}
                      onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                      placeholder="••••••••"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm_password">Confirmar Nueva Contraseña</Label>
                    <Input
                      id="confirm_password"
                      data-testid="profile-confirm-password"
                      type="password"
                      value={formData.confirm_password}
                      onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                      placeholder="••••••••"
                    />
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="pt-4">
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="w-full md:w-auto bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                  data-testid="profile-save-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? 'Guardando...' : 'Guardar Cambios'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Motorcycles Section */}
        <Card className="border-0 shadow-xl" data-testid="motorcycles-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <Bike className="w-6 h-6 text-orange-500" />
                  Mis Motocicletas
                </CardTitle>
                <CardDescription>Gestiona tus motocicletas para las inscripciones</CardDescription>
              </div>
              <Button
                onClick={handleAddNewMoto}
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                data-testid="add-motorcycle-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Añadir Moto
              </Button>
            </div>
          </CardHeader>

          <CardContent>
            {motorcycles.length === 0 ? (
              <div className="text-center py-8 text-gray-500" data-testid="no-motorcycles">
                <Bike className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                <p>No tienes motocicletas registradas</p>
                <p className="text-sm">Añade una para facilitar tus inscripciones</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="motorcycles-list">
                {motorcycles.map((moto) => (
                  <Card key={moto.id} className="bg-orange-50" data-testid={`motorcycle-${moto.id}`}>
                    <CardContent className="pt-6">
                      <div className="space-y-2">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-semibold text-lg">{moto.brand} {moto.model}</p>
                            <p className="text-sm text-gray-600">Matrícula: {moto.plate}</p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditMoto(moto)}
                              data-testid={`edit-moto-${moto.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteMoto(moto.id)}
                              data-testid={`delete-moto-${moto.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                        <div className="text-sm text-gray-600">
                          <p>Año: {moto.year}</p>
                          <p>Kilómetros: {moto.kilometers.toLocaleString()}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Motorcycle Dialog */}
        <Dialog open={showMotoDialog} onOpenChange={setShowMotoDialog}>
          <DialogContent className="max-w-md" data-testid="motorcycle-dialog">
            <DialogHeader>
              <DialogTitle>{editingMoto ? 'Editar Motocicleta' : 'Añadir Motocicleta'}</DialogTitle>
              <DialogDescription>Completa los datos de tu motocicleta</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleMotoSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="moto_brand">Marca *</Label>
                <Input
                  id="moto_brand"
                  data-testid="moto-brand-input"
                  value={motoFormData.brand}
                  onChange={(e) => setMotoFormData({ ...motoFormData, brand: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="moto_model">Modelo *</Label>
                <Input
                  id="moto_model"
                  data-testid="moto-model-input"
                  value={motoFormData.model}
                  onChange={(e) => setMotoFormData({ ...motoFormData, model: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="moto_plate">Matrícula *</Label>
                <Input
                  id="moto_plate"
                  data-testid="moto-plate-input"
                  value={motoFormData.plate}
                  onChange={(e) => setMotoFormData({ ...motoFormData, plate: e.target.value })}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="moto_year">Año *</Label>
                  <Input
                    id="moto_year"
                    data-testid="moto-year-input"
                    type="number"
                    value={motoFormData.year}
                    onChange={(e) => setMotoFormData({ ...motoFormData, year: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="moto_kilometers">Kilómetros *</Label>
                  <Input
                    id="moto_kilometers"
                    data-testid="moto-kilometers-input"
                    type="number"
                    value={motoFormData.kilometers}
                    onChange={(e) => setMotoFormData({ ...motoFormData, kilometers: e.target.value })}
                    required
                  />
                </div>
              </div>
              
              <Separator className="my-4" />
              <p className="text-sm text-gray-600 font-semibold">Documentos (Opcional)</p>
              <p className="text-xs text-gray-500">Sube los documentos ahora y estarán disponibles al inscribirte en eventos</p>
              
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="moto_circulation_permit" className="text-sm">
                    Permiso de Circulación
                  </Label>
                  <Input
                    id="moto_circulation_permit"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setMotoDocuments({ 
                      ...motoDocuments, 
                      circulation_permit: e.target.files[0] 
                    })}
                    className="cursor-pointer"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="moto_technical_sheet" className="text-sm">
                    Ficha Técnica
                  </Label>
                  <Input
                    id="moto_technical_sheet"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setMotoDocuments({ 
                      ...motoDocuments, 
                      technical_sheet: e.target.files[0] 
                    })}
                    className="cursor-pointer"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="moto_insurance" className="text-sm">
                    Seguro
                  </Label>
                  <Input
                    id="moto_insurance"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setMotoDocuments({ 
                      ...motoDocuments, 
                      insurance: e.target.files[0] 
                    })}
                    className="cursor-pointer"
                  />
                </div>
              </div>
              
              <Button
                type="submit"
                className="w-full"
                disabled={loading}
                data-testid="save-motorcycle-btn"
              >
                {loading ? 'Guardando...' : editingMoto ? 'Actualizar' : 'Añadir'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Offline Maps Section */}
        <div id="tracker"><TrackerSetup /></div>

        <OfflineMapManager />

        {/* Account Info */}
        <Card className="border-0 shadow-lg bg-orange-50" data-testid="account-info">
          <CardContent className="pt-6">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Tipo de cuenta:</span>
                <span className="font-semibold">{user.is_admin ? 'Administrador' : 'Usuario'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">ID de usuario:</span>
                <span className="font-mono text-xs">{user.id}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* User Tracks Section */}
      <div id="my-tracks">
        <UserTracksSection />
      </div>
    </div>
  );
}
